import type { ResourceType } from '@cotera/api';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import type { ResourceAccessStatus } from './watchables-factory';
import { useResourceUpdatedWatchable } from './use-resource-updated-watchable';

export type { ResourceAccessStatus };

/**
 * Live read-access verdict for one resource, for callers that must decide what
 * to render before mounting a (heavy, throwing) resource page — chiefly
 * `ChatArtifactAccessGate`.
 *
 * Backed by a `ResourceUpdated` watchable rather than a one-shot query, so the
 * verdict tracks the resource: one seeded before its row exists flips to
 * `accessible` when the row lands, without a reload.
 */
export function useResourceAccess(
  type: ResourceType,
  id: string
): ResourceAccessStatus {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  // Nothing to probe without an id, and nothing that a later event could
  // resolve either — report it as unreachable rather than spinning forever.
  const enabled = id !== '';

  return useResourceUpdatedWatchable<ResourceAccessStatus>(
    `${orgId}:resource-access:${type}:${id}`,
    (factory) =>
      factory.makeResourceAccessWatchable({ orgId, type, id, client }),
    { enabled, disabledValue: 'denied' }
  );
}
